import { useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/hooks/useAuth";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { pollSigningStatus } from "@/domains/documents/signing/repository";
import {
  listContainerContents,
  listDocumentsByIds,
  listSignedContainers,
  listSigningSessions,
  SIGNING_LIST_LIMIT,
} from "./repository";
import { mergeSigningItems } from "./status";
import type { SigningItemStatus, SigningListItem } from "./types";

interface SigningScope {
  orgId?: string;
  userId?: number;
  /** Both queries are RLS-scoped, so they wait for the org context. */
  enabled: boolean;
  /** Org context settled with NO org — terminal, must not read as loading. */
  orgMissing: boolean;
}

function useSigningScope(buildingId: number | null): SigningScope {
  const { userProfile } = useAuth();
  const { currentOrganization, isReady } = useOrganizationContext();
  const authStore = useAuthStore();
  const orgId = currentOrganization?.id;
  // NOT gated on useSigningVisible, though the page's redirect is: these rows
  // are now what that redirect asks about (shouldLeaveSigningPage), so a scope
  // that waited for the flag could only ever answer "nothing here". Costs an
  // off org's direct-link visitor two RLS-scoped reads before it bounces them.
  // Still waits for RBAC, which decides the platform-admin half of the flag:
  // `isReady` flips after the FIRST org's permissions load and stays true
  // across an org switch, whose fetch is fired without being awaited.
  const rbacSettled = orgId == null || authStore.hasLoadedPermissions(orgId);
  return {
    orgId,
    userId: userProfile?.id,
    enabled: buildingId != null && isReady && orgId != null && rbacSettled,
    orgMissing: isReady && orgId == null,
  };
}

function useSignedContainers(
  buildingId: number | null,
  projectId: number | null,
  scope: SigningScope
) {
  return useQuery({
    // userId in every signing key: the QueryClient is a module-level singleton
    // with a 5-min staleTime, so a same-tab account switch would otherwise be
    // served the previous user's rows as fresh (logout also clears the cache;
    // the key is the belt to that suspender). projectId is in the key because
    // it is part of the query's scope, and `null` (unfiled) is a real scope
    // rather than "everything".
    queryKey: ["signing", "containers", scope.orgId, buildingId, projectId, scope.userId],
    queryFn: () => listSignedContainers(buildingId as number, projectId, scope.userId ?? null),
    enabled: scope.enabled,
  });
}

function useSigningChains(
  buildingId: number | null,
  projectId: number | null,
  scope: SigningScope
) {
  return useQuery({
    queryKey: ["signing", "sessions", scope.orgId, buildingId, projectId, scope.userId],
    queryFn: () => listSigningSessions(buildingId as number, projectId, scope.userId ?? null),
    enabled: scope.enabled,
  });
}

/**
 * A disabled query reports isLoading=false, so the pre-`isReady` window
 * (org + RBAC load) must count as loading or the empty state flashes.
 * A null buildingId (bad route param) or a settled context with no org
 * (e.g. a platform admin outside any org) will never load — don't spin.
 */
export function isSigningListLoading(
  buildingId: number | null,
  scope: Pick<SigningScope, "enabled" | "orgMissing">,
  queriesLoading: boolean
): boolean {
  if (buildingId == null || scope.orgMissing) return false;
  return !scope.enabled || queriesLoading;
}

/**
 * A full page means the DB may hold more rows than the cap — the search box
 * only sees what loaded, so the UI must say "latest N" instead of letting
 * "no matches" read as "doesn't exist".
 */
export function isSigningListTruncated(
  containers: unknown[] | undefined,
  sessions: unknown[] | undefined
): boolean {
  return (
    (containers?.length ?? 0) >= SIGNING_LIST_LIMIT || (sessions?.length ?? 0) >= SIGNING_LIST_LIMIT
  );
}

/** Snappy: this window lasts seconds and ends in "Signed". */
const FINALIZING_POLL_MS = 3_000;
/** Slow: a chain waiting on a person changes when that person acts, not sooner. */
const IN_FLIGHT_POLL_MS = 15_000;
/**
 * How many fast ticks a finalizing session gets before it is treated as stuck.
 * 2 minutes at 3s. Finalization is a download-and-upload of one container, so
 * past this something is wrong at Dokobit or in the backend, and hammering
 * `dokobit-sign-status` every 3s — for every viewer with the page open, forever
 * — buys nothing. It degrades to the slow refresh, which still notices when the
 * hourly sweep or somebody's "Check status" resolves it.
 */
const MAX_FINALIZING_TICKS = 40;
/**
 * How many reconciles a row that still says "Waiting for YOU" gets, to prove it
 * is not in fact already signed.
 *
 * A row only becomes `finalizing` after a reconcile has recorded the signature —
 * so the very failure this poll exists for has no way back on its own. Sign a
 * solo chain and close the dialog (or navigate, or reload, or have that one read
 * 500) before its `onSignSuccess → pollNow()` lands, and the session is still
 * `awaiting_user` with your signer row `pending`: the list says "Waiting for
 * you" about a signing you have already signed, and nothing here would ever ask
 * again. Recovery then rests entirely on Dokobit's postback — which the design
 * treats as a latency optimisation, never a dependency — the hourly sweep, or
 * the user pressing "Check status".
 *
 * Small on purpose, and NOT the finalizing budget: a chain genuinely waiting on
 * a person cannot be hurried, so this is a fixed handful of reads per session
 * per page mount, not a poll. Two is enough to cover the one lost read.
 */
const MAX_SELF_HEAL_TICKS = 2;
/**
 * The other way a session stops being worth polling: the reads themselves never
 * land (offline, a 5xx, the function gone).
 *
 * Both budgets above are spent in ANSWERS on purpose — a failed read has not
 * asked the question, and charging it means a bad two minutes burns a
 * `finalizing` row's whole budget without one answer, after which nothing
 * re-checks that session for the rest of the page mount. Two failed reads did
 * the same to the self-heal budget. So consecutive failures get their own,
 * larger budget, reset by any read that answers: flakiness costs nothing and a
 * dead endpoint still degrades to the slow refresh instead of polling forever.
 * 5 minutes at the fast cadence. Same split as the sign dialog's
 * MAX_SEALING_TICKS / MAX_SEALING_FAILURES.
 */
const MAX_POLL_FAILURES = 100;
/**
 * How many sessions one tick may reconcile.
 *
 * The per-session budget bounds how LONG a session is driven, not how WIDE the
 * tick is: `driving` comes off a list capped at SIGNING_LIST_LIMIT (500), and a
 * building with many stuck chains — which stay `finalizing` for their whole life
 * — would fan out hundreds of concurrent `dokobit-sign-status` calls every 3s,
 * per tab, per viewer, each a container download and upload server-side.
 * Sessions are taken least-read-first, so this is a round-robin: a chain that
 * has just entered `finalizing` (0 reads) goes to the front and is reconciled on
 * the next tick however long the queue is, while stuck ones share what is left.
 */
const MAX_SESSIONS_PER_TICK = 5;

/** What a session has spent: reads that ANSWERED, and its current failure streak. */
export interface SessionPollBudget {
  answered: number;
  failedInARow: number;
}

const NOTHING_SPENT: SessionPollBudget = { answered: 0, failedInARow: 0 };

/**
 * Which sessions this tick drives `dokobit-sign-status` for, and how long until
 * the next one. Pure so the caps can be asserted without standing up the hook.
 *
 * The budget is PER SESSION. One counter for the whole set does not bound
 * anything: it has to be reset when the set changes (or a new session inherits
 * a spent budget), and that reset hands every already-stuck session a fresh 2
 * minutes each time an unrelated one enters or leaves `finalizing` — which on a
 * busy list is continuous, and is exactly the hammering the cap exists to stop.
 *
 * Both classes share one counter, so a row that self-heals into `finalizing`
 * carries its two spent ticks into the 40 — which is the point: they were reads
 * of the same session.
 */
export function signingPollPlan(
  finalizingIds: string,
  spent: ReadonlyMap<string, SessionPollBudget>,
  awaitingYouIds = ""
): { driving: string[]; intervalMs: number } {
  const budgetOf = (sessionId: string) => spent.get(sessionId) ?? NOTHING_SPENT;
  const attempts = (sessionId: string) =>
    budgetOf(sessionId).answered + budgetOf(sessionId).failedInARow;
  const withinBudget = (ids: string, cap: number) =>
    (ids === "" ? [] : ids.split(",")).filter((sessionId) => {
      const budget = budgetOf(sessionId);
      return budget.answered < cap && budget.failedInARow < MAX_POLL_FAILURES;
    });
  const eligible = [
    ...withinBudget(finalizingIds, MAX_FINALIZING_TICKS),
    ...withinBudget(awaitingYouIds, MAX_SELF_HEAL_TICKS),
  ];
  // Stable sort, so sessions that have had the same number of reads keep list
  // order (newest first) rather than shuffling between ticks.
  const driving = [...eligible]
    .sort((a, b) => attempts(a) - attempts(b))
    .slice(0, MAX_SESSIONS_PER_TICK);
  return { driving, intervalMs: eligible.length > 0 ? FINALIZING_POLL_MS : IN_FLIGHT_POLL_MS };
}

/** Statuses a row can still move out of on its own. */
const IN_FLIGHT_STATUSES: ReadonlySet<SigningItemStatus> = new Set<SigningItemStatus>([
  "awaiting_you",
  "awaiting_others",
  "finalizing",
]);

/**
 * Keep the page honest while a signing is still moving. Two problems, one timer.
 *
 * 1. The page had NO self-refresh at all — no interval, no realtime channel, and
 *    the 5-minute global `staleTime`. Its only route to fresh data was somebody
 *    invalidating `["signing"]`, and the sign dialog only does that once its own
 *    poll observes `completed`. A solo signer closes that dialog the moment
 *    Dokobit says "signed", which kills the poll — so the session the backend
 *    completed a second later stayed on screen as in-flight until a reload.
 *
 * 2. A `finalizing` session is one reconcile short of `completed`, and
 *    `dokobit-sign-status` IS that reconcile — it re-reads Dokobit's status.json
 *    and finalizes the moment the container exists. Driving it here is what
 *    makes the row reach "Signed" by itself instead of waiting on a postback we
 *    do not control (or, if that is ever lost, on the hourly sweep). It is the
 *    same call the panel's "Check status" button makes, on a timer.
 *
 * Only `finalizing` sessions are driven on the timer. A chain genuinely waiting
 * on a person cannot be hurried, so calling the function for every listed chain,
 * on every tick, for every viewer, would cost real money to change nothing. The
 * one exception is a row that says "Waiting for YOU", which gets a couple of
 * reads to prove that claim (MAX_SELF_HEAL_TICKS) — it is the only shape whose
 * own status may be the stale thing.
 *
 *   ponytail: polling, not a `postgres_changes` subscription. The window this
 *   exists for lasts seconds, and realtime is never a source of truth here
 *   anyway (see the repo's realtime note). Reach for a channel only if the page
 *   ever has to react to something slower than it polls.
 */
function useSigningAutoRefresh(items: SigningListItem[], enabled: boolean): void {
  const queryClient = useQueryClient();

  // Joined into a string so the effect keys off the SET of sessions to drive
  // rather than the fresh array identity every render produces.
  const sessionIdsWithStatus = (status: SigningItemStatus) =>
    items
      .filter((item) => item.status === status && item.sessionId != null)
      .map((item) => item.sessionId as string)
      .join(",");
  const finalizingIds = sessionIdsWithStatus("finalizing");
  const awaitingYouIds = sessionIdsWithStatus("awaiting_you");
  const anyInFlight = items.some((item) => IN_FLIGHT_STATUSES.has(item.status));

  /**
   * What each session has spent — answers and its failure streak — OUTSIDE the
   * effect because the effect is rebuilt
   * every time the finalizing set changes and a budget that dies with it is no
   * budget at all. A ref rather than state: nothing renders off these counts,
   * and state would have to be reset from an effect — the cascading render
   * React tells you not to write.
   *
   *   ponytail: never pruned. It is capped by the page's own SIGNING_LIST_LIMIT
   *   and dies with the component; evicting a session that left `finalizing`
   *   would just hand it a fresh budget if the row flickers back.
   */
  const spent = useRef(new Map<string, SessionPollBudget>());

  useEffect(() => {
    if (!enabled || !anyInFlight) return;
    let timer = 0;
    let cancelled = false;

    /** Charge one read to its session — see MAX_POLL_FAILURES for the split. */
    function charge(sessionId: string, answered: boolean) {
      const budget = spent.current.get(sessionId) ?? NOTHING_SPENT;
      spent.current.set(
        sessionId,
        answered
          ? { answered: budget.answered + 1, failedInARow: 0 }
          : { ...budget, failedInARow: budget.failedInARow + 1 }
      );
    }

    // Self-rescheduling rather than setInterval, for two reasons. The gap
    // between ticks is part of what the plan decides — it stretches from 3s to
    // 15s the moment the budget runs out — and a tick that outlives its own
    // interval must not overlap the next one: `pollSigningStatus` runs
    // `reconcileChain` server-side (a container download and upload), which in
    // the slow-finalize case this poll exists for is exactly when it would.
    function schedule(ms: number) {
      timer = window.setTimeout(() => void tick(), ms);
    }

    async function tick() {
      const { driving, intervalMs } = signingPollPlan(finalizingIds, spent.current, awaitingYouIds);
      try {
        // A hidden tab is not being read; polling it spends edge-function calls
        // to resolve nothing the next foreground tick would not — and must not
        // burn the finalizing budget either.
        if (document.visibilityState !== "visible") return;
        // Charged when the read SETTLES, not before it is issued: a budget spent
        // on reads that never answered is how a bad two minutes used to leave a
        // `finalizing` row with nothing driving it for the rest of the page
        // mount — the exact failure this poll heals. Failures spend their own,
        // larger budget instead (MAX_POLL_FAILURES).
        //
        // The rejection is swallowed for the same reason it always was: this is
        // a background refresh. An error the user should see belongs to the
        // explicit "Check status" button, which reports it — a toast per tick
        // would be noise, and the next tick retries anyway.
        await Promise.all(
          driving.map((id) =>
            pollSigningStatus(id).then(
              () => charge(id, true),
              () => charge(id, false)
            )
          )
        );
        // Checked again after the poll: if the page went away while those were
        // in flight, refetching two list queries nobody is looking at is a
        // round-trip spent on a screen that no longer exists.
        if (!cancelled) await queryClient.invalidateQueries({ queryKey: ["signing"] });
      } catch {
        // Swallowed for the same reason the poll's own failures are: this is a
        // background refresh and the next tick retries. `invalidateQueries` IS
        // the refetch, so it can reject, and a throw escaping `void tick()`
        // would end the page's auto-refresh for the rest of the SPA session —
        // silently, and it is staleness this hook exists to prevent.
      } finally {
        // `finally`, so a tick can never throw its way out of the loop.
        //
        // The re-arm is also guarded, because those awaits mean this resumes
        // after the effect may already have been torn down — by the very
        // invalidation it just awaited, since a row leaving `finalizing` is what
        // changes `finalizingIds`. Re-arming then would hand a live timer to a
        // cleanup that has already run: the id it cleared is the old one, and
        // this closure is the only thing holding the new one. One orphan loop
        // per teardown, forever, each with a stale set.
        if (!cancelled) schedule(intervalMs);
      }
    }

    schedule(signingPollPlan(finalizingIds, spent.current, awaitingYouIds).intervalMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [enabled, anyInFlight, finalizingIds, awaitingYouIds, queryClient]);
}

/**
 * The Signing page's list for one project of a building: finished containers
 * plus every in-flight chain the viewer can see — the ones they sent, the ones
 * they were invited to, and the ones they manage — PLUS the building's unfiled
 * rows (`project_id IS NULL`), which the repository flags so the UI can label
 * them. Passing `projectId === null` narrows to those unfiled rows alone.
 */
export function useSigningItems(buildingId: number | null, projectId: number | null) {
  const scope = useSigningScope(buildingId);
  const containers = useSignedContainers(buildingId, projectId, scope);
  const sessions = useSigningChains(buildingId, projectId, scope);

  const items = useMemo(
    () => mergeSigningItems(sessions.data, containers.data),
    [sessions.data, containers.data]
  );

  useSigningAutoRefresh(items, scope.enabled);

  return {
    items,
    isLoading: isSigningListLoading(buildingId, scope, containers.isLoading || sessions.isLoading),
    error: containers.error ?? sessions.error,
    isTruncated: isSigningListTruncated(containers.data, sessions.data),
  };
}

/** The files inside a signed container (`signed_container_sources`). */
export function useContainerContents(containerDocumentId: number | null) {
  const { userProfile } = useAuth();
  return useQuery({
    queryKey: ["signing", "container-contents", userProfile?.id, containerDocumentId],
    queryFn: () => listContainerContents(containerDocumentId as number),
    enabled: containerDocumentId != null,
  });
}

/** The documents queued in an in-flight session (`source_doc_ids`). */
export function useSessionDocuments(sessionId: string | null, documentIds: number[]) {
  const { userProfile } = useAuth();
  return useQuery({
    queryKey: ["signing", "session-documents", userProfile?.id, sessionId],
    queryFn: () => listDocumentsByIds(documentIds),
    enabled: sessionId != null && documentIds.length > 0,
  });
}
